import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Box, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import GalleryAssetCard from "./GalleryAssetCard";
import { buildGalleryPanels, getAssetKey, getAssetKind } from "./galleryUtils";
import useGalleryAssetShapeMap from "./useGalleryAssetShapeMap";

function GalleryAssetGrid(props) {
  const {
    assets,
    onOpenAsset,
    availableHeight,
    keyboardEnabled = true,
  } = props;
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const scrollRef = useRef(null);
  const dragStateRef = useRef({
    isDragging: false,
    startX: 0,
    startScrollLeft: 0,
    moved: false,
  });
  const suppressOpenUntilRef = useRef(0);
  const keyScrollStateRef = useRef({
    activeKey: null,
    holdTimeoutId: null,
    scrollIntervalId: null,
    didHoldScroll: false,
  });
  const pointerScrollStateRef = useRef({
    direction: 0,
    holdTimeoutId: null,
    scrollIntervalId: null,
    didHoldScroll: false,
  });
  const [isDragging, setIsDragging] = useState(false);
  const shapeMap = useGalleryAssetShapeMap(assets);

  const panelHeight = Math.max(availableHeight, isPhone ? 320 : 420);
  const panelWidth = Math.round(panelHeight * (isPhone ? 1.5 : 1.78));
  const panels = useMemo(
    () => buildGalleryPanels(assets, shapeMap),
    [assets, shapeMap]
  );
  const loopPanels = useMemo(
    () =>
      panels.length
        ? [...panels, ...panels, ...panels].map((panel, index) => ({
            ...panel,
            loopIndex: index,
            originalIndex: index % panels.length,
          }))
        : [],
    [panels]
  );
  const loopWidth = panels.length * panelWidth;
  const holdStep = Math.max(5, Math.round(panelWidth * 0.01125));
  const jumpStep = Math.round(panelWidth * 0.01);

  const scrollByDirection = (
    direction,
    behavior = "smooth",
    amount = jumpStep
  ) => {
    scrollRef.current?.scrollBy({
      left: direction * amount,
      behavior,
    });
  };

  const clearPointerScroll = () => {
    if (pointerScrollStateRef.current.holdTimeoutId) {
      window.clearTimeout(pointerScrollStateRef.current.holdTimeoutId);
    }

    if (pointerScrollStateRef.current.scrollIntervalId) {
      window.clearInterval(pointerScrollStateRef.current.scrollIntervalId);
    }

    pointerScrollStateRef.current = {
      direction: 0,
      holdTimeoutId: null,
      scrollIntervalId: null,
      didHoldScroll: false,
    };
  };

  const startPointerScroll = (direction) => {
    clearPointerScroll();
    pointerScrollStateRef.current.direction = direction;
    pointerScrollStateRef.current.holdTimeoutId = window.setTimeout(() => {
      pointerScrollStateRef.current.didHoldScroll = true;
      pointerScrollStateRef.current.scrollIntervalId = window.setInterval(
        () => {
          scrollByDirection(direction, "auto", holdStep);
        },
        16
      );
    }, 180);
  };

  const endPointerScroll = (direction) => {
    const didHoldScroll = pointerScrollStateRef.current.didHoldScroll;
    const activeDirection = pointerScrollStateRef.current.direction;
    clearPointerScroll();

    if (!didHoldScroll && activeDirection === direction) {
      scrollByDirection(direction);
    }
  };

  useEffect(() => {
    if (!scrollRef.current || !panels.length) {
      return;
    }

    scrollRef.current.scrollLeft = loopWidth;
  }, [loopWidth, panels.length]);

  useEffect(() => {
    if (!scrollRef.current || !panels.length || !loopWidth) {
      return undefined;
    }

    const scrollElement = scrollRef.current;

    const handleScroll = () => {
      if (!loopWidth) {
        return;
      }

      if (scrollElement.scrollLeft < loopWidth * 0.5) {
        scrollElement.scrollLeft += loopWidth;
      } else if (scrollElement.scrollLeft > loopWidth * 1.5) {
        scrollElement.scrollLeft -= loopWidth;
      }
    };

    scrollElement.addEventListener("scroll", handleScroll);
    return () => {
      scrollElement.removeEventListener("scroll", handleScroll);
    };
  }, [loopWidth, panels.length]);

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement || isPhone) {
      return undefined;
    }

    const handleWheel = (event) => {
      const horizontalDelta =
        Math.abs(event.deltaX) > Math.abs(event.deltaY)
          ? event.deltaX
          : event.deltaY;

      if (horizontalDelta === 0) {
        return;
      }

      event.preventDefault();
      scrollElement.scrollBy({
        left: horizontalDelta,
        behavior: "auto",
      });
    };

    scrollElement.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      scrollElement.removeEventListener("wheel", handleWheel);
    };
  }, [isPhone]);

  useEffect(() => {
    if (!keyboardEnabled) {
      return undefined;
    }

    const clearKeyScroll = () => {
      if (keyScrollStateRef.current.holdTimeoutId) {
        window.clearTimeout(keyScrollStateRef.current.holdTimeoutId);
      }

      if (keyScrollStateRef.current.scrollIntervalId) {
        window.clearInterval(keyScrollStateRef.current.scrollIntervalId);
      }

      keyScrollStateRef.current = {
        activeKey: null,
        holdTimeoutId: null,
        scrollIntervalId: null,
        didHoldScroll: false,
      };
    };

    const handleKeyDown = (event) => {
      if (!scrollRef.current) {
        return;
      }

      if (
        event.target instanceof HTMLElement &&
        (event.target.tagName === "INPUT" ||
          event.target.tagName === "TEXTAREA" ||
          event.target.isContentEditable)
      ) {
        return;
      }

      if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") {
        return;
      }

      event.preventDefault();

      if (keyScrollStateRef.current.activeKey === event.key) {
        return;
      }

      clearKeyScroll();

      const direction = event.key === "ArrowRight" ? 1 : -1;
      keyScrollStateRef.current.activeKey = event.key;
      keyScrollStateRef.current.holdTimeoutId = window.setTimeout(() => {
        keyScrollStateRef.current.didHoldScroll = true;
        keyScrollStateRef.current.scrollIntervalId = window.setInterval(() => {
          scrollByDirection(direction, "auto", holdStep);
        }, 16);
      }, 180);
    };

    const handleKeyUp = (event) => {
      if (
        event.key !== "ArrowRight" &&
        event.key !== "ArrowLeft" &&
        keyScrollStateRef.current.activeKey !== event.key
      ) {
        return;
      }

      const activeKey = keyScrollStateRef.current.activeKey;
      const didHoldScroll = keyScrollStateRef.current.didHoldScroll;
      clearKeyScroll();

      if (!scrollRef.current || didHoldScroll || activeKey !== event.key) {
        return;
      }

      const direction = event.key === "ArrowRight" ? 1 : -1;
      scrollByDirection(direction);
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", clearKeyScroll);
    return () => {
      clearKeyScroll();
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", clearKeyScroll);
    };
  }, [holdStep, jumpStep, keyboardEnabled, panelWidth]);

  useEffect(
    () => () => {
      clearPointerScroll();
    },
    []
  );

  const handlePointerDown = (event) => {
    if (!scrollRef.current || event.button !== 0) {
      return;
    }

    dragStateRef.current = {
      isDragging: true,
      startX: event.clientX,
      startScrollLeft: scrollRef.current.scrollLeft,
      moved: false,
    };
    setIsDragging(true);
  };

  const handlePointerMove = (event) => {
    if (!dragStateRef.current.isDragging || !scrollRef.current) {
      return;
    }

    const deltaX = event.clientX - dragStateRef.current.startX;
    if (Math.abs(deltaX) > 6) {
      dragStateRef.current.moved = true;
    }
    scrollRef.current.scrollLeft =
      dragStateRef.current.startScrollLeft - deltaX;
  };

  const resetDragState = () => {
    dragStateRef.current.isDragging = false;
    dragStateRef.current.moved = false;
    setIsDragging(false);
  };

  const handlePointerUp = (event) => {
    const releaseTarget = event.target instanceof Element ? event.target : null;
    const assetButton = releaseTarget?.closest?.("[data-gallery-asset-index]");

    if (!dragStateRef.current.moved && assetButton) {
      const assetIndex = Number(
        assetButton.getAttribute("data-gallery-asset-index")
      );
      if (Number.isInteger(assetIndex) && assetIndex >= 0) {
        onOpenAsset(assetIndex);
      }
    }

    if (dragStateRef.current.moved) {
      suppressOpenUntilRef.current = Date.now() + 150;
    }

    resetDragState();
  };

  const handlePointerLeave = () => {
    if (dragStateRef.current.moved) {
      suppressOpenUntilRef.current = Date.now() + 150;
    }

    resetDragState();
  };

  if (!assets.length) {
    return (
      <Box
        sx={{
          backgroundColor: "rgba(255,255,255,0.78)",
          borderRadius: "28px",
          p: { xs: 3, sm: 5 },
          textAlign: "center",
          boxShadow: "0 18px 34px rgba(14,18,23,0.08)",
        }}
      >
        <Typography variant="h5" sx={{ mb: 1 }}>
          Gallery coming soon
        </Typography>
        <Typography variant="body1" color="text.secondary">
          No gallery images are live yet.
        </Typography>
      </Box>
    );
  }

  if (isPhone) {
    return (
      <Box
        sx={{
          width: "100%",
          height: `${panelHeight}px`,
          minHeight: `${panelHeight}px`,
          overflowY: "auto",
          overflowX: "hidden",
          touchAction: "pan-y",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: 0,
            width: "100%",
          }}
        >
          {assets.map((asset, assetIndex) => {
            const assetKey = getAssetKey(asset, assetIndex);
            const shape = shapeMap[assetKey] || getAssetKind(asset);
            const aspectRatio =
              shape === "pdf" || shape === "vertical"
                ? "1080 / 1920"
                : shape === "square"
                ? "1 / 1"
                : "16 / 9";

            return (
              <GalleryAssetCard
                key={assetKey}
                asset={asset}
                assetIndex={assetIndex}
                onOpen={() => onOpenAsset(assetIndex)}
                sx={{
                  aspectRatio,
                }}
              />
            );
          })}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: `${panelHeight}px`,
        minHeight: `${panelHeight}px`,
      }}
    >
      <Box
        ref={scrollRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerLeave}
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 0,
          alignItems: "stretch",
          height: "100%",
          minHeight: "100%",
          width: "100%",
          overflowX: "auto",
          overflowY: "hidden",
          cursor: isDragging ? "grabbing" : "grab",
          touchAction: "pan-x",
          userSelect: "none",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        {loopPanels.map((panel) => (
          <Box
            key={`gallery-panel-${panel.loopIndex}-${panel.originalIndex}`}
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gridTemplateRows: "repeat(2, 1fr)",
              gap: 0,
              width: `${panelWidth}px`,
              minWidth: `${panelWidth}px`,
              height: "100%",
              flexShrink: 0,
            }}
          >
            {panel.items.map((item) => (
              <GalleryAssetCard
                key={`${panel.loopIndex}-${item.column}-${item.row}-${getAssetKey(
                  item.asset,
                  item.assetIndex
                )}`}
                asset={item.asset}
                assetIndex={item.assetIndex}
                layout={item}
                onOpen={() => {
                  if (Date.now() < suppressOpenUntilRef.current) {
                    return;
                  }

                  onOpenAsset(item.assetIndex);
                }}
              />
            ))}
          </Box>
        ))}
      </Box>

      {[
        { direction: -1, side: "left", Icon: ChevronLeftIcon },
        { direction: 1, side: "right", Icon: ChevronRightIcon },
      ].map(({ direction, side, Icon }) => (
        <Box
          key={side}
          component="button"
          type="button"
          aria-label={
            side === "left" ? "Scroll gallery left" : "Scroll gallery right"
          }
          onPointerDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
            startPointerScroll(direction);
          }}
          onPointerUp={(event) => {
            event.preventDefault();
            event.stopPropagation();
            endPointerScroll(direction);
          }}
          onPointerLeave={() => {
            clearPointerScroll();
          }}
          onPointerCancel={() => {
            clearPointerScroll();
          }}
          sx={{
            position: "absolute",
            top: "50%",
            [side]: { xs: 8, sm: 14 },
            transform: "translateY(-50%)",
            zIndex: 2,
            width: { xs: 42, sm: 52 },
            height: { xs: 72, sm: 96 },
            border: "none",
            borderRadius: 0,
            background: "transparent",
            color: "rgba(237,243,251,0.9)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "opacity 180ms ease, transform 180ms ease",
            "&:hover": {
              opacity: 0.78,
            },
          }}
        >
          <Icon sx={{ fontSize: { xs: 28, sm: 34 } }} />
        </Box>
      ))}
    </Box>
  );
}

export default GalleryAssetGrid;
